import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';

import { Injectable, signal } from '@angular/core';

export interface User {
  userId: string;
  username: string;
  email?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  user = signal<User | null>(null);
  token = signal<string | null>(null);

  async login(email: string, password: string): Promise<void> {
    const result = await signIn({ username: email, password });

    if (result.isSignedIn !== true) {
      throw new Error('Login not completed. Additional steps required.');
    }

    const currentUser = await getCurrentUser();
    const session = await fetchAuthSession();

    this.user.set({
      userId: currentUser.userId,
      username: currentUser.username,
      email: currentUser.signInDetails?.loginId,
    });

    this.token.set(session.tokens?.accessToken?.toString() ?? null);
  }

  async logout(): Promise<void> {
    await signOut();
    this.user.set(null);
    this.token.set(null);
  }

  async checkAuth(): Promise<void> {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      this.user.set({
        userId: currentUser.userId,
        username: currentUser.username,
        email: currentUser.signInDetails?.loginId,
      });

      this.token.set(session.tokens?.accessToken?.toString() ?? null);
    } catch {
      this.user.set(null);
      this.token.set(null);
    }
  }

  isAuthenticated(): boolean {
    return !!this.token();
  }

  async forgotPassword(email: string): Promise<void> {
    await resetPassword({ username: email });
  }

  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  }
}
